import React, { useState, useEffect } from 'react';
import {
  Modal, Spin, message, Descriptions, Tag, Button, Space, 
  Divider, Card, Row, Col, Typography, Alert, Popconfirm, Table,
} from 'antd';
import { 
  FileTextOutlined, PrinterOutlined, ReloadOutlined, 
  DownloadOutlined, EyeOutlined,
} from '@ant-design/icons';
import { getVoucher, regenerateVoucher } from '../api';
import { splitModeLabel } from '../../../components/PaymentSplitCells';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const VoucherDetailsModal = ({
  open,
  onCancel,
  voucherId,
  onRegenerateSuccess,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  
  // State
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // Load voucher data 
  const loadVoucher = async () => {
    if (!voucherId) return;
    
    setLoading(true);
    try {
      const result = await getVoucher(voucherId);
      setVoucher(result.data);
    } catch (error) {
      console.error('Load voucher error:', error);
      messageApi.error('Failed to load voucher details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && voucherId) {
      loadVoucher();
    } else {
      setVoucher(null);
    }
  }, [open, voucherId]);

  // Handle PDF regeneration
  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const result = await regenerateVoucher(voucherId, 'User requested regeneration');
      
      messageApi.success('Voucher PDF regenerated successfully');
      
      // Refresh voucher data
      await loadVoucher();
      
      // Notify parent component
      onRegenerateSuccess?.(result.data);
      
    } catch (error) {
      console.error('Regenerate error:', error);
      messageApi.error(error?.response?.data?.message || 'Failed to regenerate voucher');
    } finally {
      setRegenerating(false);
    }
  };

  // Handle print
  const handlePrint = () => {
    if (voucher?.pdfUrl) {
      // Open PDF in new tab for printing
      window.open(voucher.pdfUrl, '_blank');
    } else {
      messageApi.warning('PDF URL not available');
    }
  };

  // Handle download
  const handleDownload = () => {
    if (voucher?.pdfUrl) {
      const link = document.createElement('a');
      link.href = voucher.pdfUrl;
      link.download = `voucher-${voucher.voucherId}.pdf`;
      link.click();
    } else {
      messageApi.warning('PDF URL not available');
    }
  };

  // Render loading state
  if (loading) {
    return (
      <Modal
        title="Loading Voucher..."
        open={open}
        onCancel={onCancel}
        footer={null}
        width={800}
      >
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
        </div>
      </Modal>
    );
  }

  // Render not found state
  if (open && !loading && !voucher) {
    return (
      <Modal
        title="Voucher Not Found"
        open={open}
        onCancel={onCancel}
        footer={<Button onClick={onCancel}>Close</Button>}
        width={500}
      >
        <Alert
          type="error"
          showIcon
          message="The requested voucher could not be found."
        />
      </Modal>
    );
  }

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR', 
      maximumFractionDigits: 2 
    }).format(amount || 0);

  // Format amount as whole rupee without decimals
  const formatWholeAmount = (price) =>
    new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR',
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    }).format(price || 0);

  // Per-tranche breakdown, joined in by the API from the live payment record: the
  // voucher's own paymentMode collapses to 'SPLIT' and it keeps at most one
  // reference, so this is the only place the UTR of each tranche can be read.
  const splits = voucher?.paymentSplits || [];

  return (
    <>
      {contextHolder}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            Voucher Details — {voucher?.voucherId}
          </Space>
        }
        open={open}
        onCancel={onCancel}
        width={900}
        footer={[
          <Button key="close" onClick={onCancel}>
            Close
          </Button>,
          <Button
            key="download"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            disabled={!voucher?.pdfUrl}
          >
            Download PDF
          </Button>,
          <Button
            key="print"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            disabled={!voucher?.pdfUrl}
          >
            Print
          </Button>,
          <Popconfirm
            key="regenerate"
            title="Regenerate Voucher PDF"
            description="This will create a new PDF with the same data. The old version will be stored in regeneration history. Continue?"
            onConfirm={handleRegenerate}
            okText="Yes, Regenerate"
            okType="primary"
            cancelText="Cancel"
          >
            <Button
              icon={<ReloadOutlined />}
              loading={regenerating}
              type="primary"
              ghost
            >
              Regenerate PDF
            </Button>
          </Popconfirm>,
        ]}
      >
        {voucher && (
          <>
            {/* Voucher Summary Info */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                  <Text type="secondary">Voucher ID</Text>
                  <br />
                  <Text strong style={{ fontSize: 16 }}>{voucher.voucherId}</Text>
                </Col>
                <Col xs={24} sm={8}>
                  <Text type="secondary">Amount</Text>
                  <br />
                  <Text strong style={{ fontSize: 16, color: '#3f8600' }}>
                    {formatWholeAmount(voucher.amount)}
                  </Text>
                </Col>
                <Col xs={24} sm={8}>
                  <Text type="secondary">Status</Text>
                  <br />
                  <Tag color={voucher.status === 'ISSUED' ? 'green' : 'orange'}>
                    {voucher.status}
                    {voucher.regenerationHistory?.length > 0 && ` (${voucher.regenerationHistory.length}x regenerated)`}
                  </Tag>
                </Col>
              </Row>
            </Card>

            {/* Voucher Details */}
            <Descriptions
              title="Voucher Information"
              bordered
              column={{ xs: 1, sm: 2, md: 2 }}
              size="small"
            >
              <Descriptions.Item label="Payment ID">
                {voucher.paymentId}
              </Descriptions.Item>
              <Descriptions.Item label="Case ID">
                {voucher.caseId?.caseId}
              </Descriptions.Item>
              
              <Descriptions.Item label="Customer">
                {voucher.caseId?.customerId?.name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {voucher.caseId?.customerId?.phone || '-'}
              </Descriptions.Item>

              <Descriptions.Item label="Recipient">
                {voucher.recipientName}
              </Descriptions.Item>
              <Descriptions.Item label="Advance Category">
                <Tag color={voucher.advanceCategory === 'LOAN_PAYMENT' ? 'blue' : 'green'}>
                  {voucher.advanceCategory === 'LOAN_PAYMENT' ? 'Loan Payment' : 'Customer Take-Home'}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Payment Mode">
                {voucher.paymentMode?.replace(/_/g, ' ')}
              </Descriptions.Item>
              {/* A split payment has one reference per tranche; those are listed in
                  the breakdown below, so this row would only ever show part of the
                  story. Shown here only when the payment went out in one piece. */}
              <Descriptions.Item label="Transaction Reference">
                {splits.length > 1 ? 'See payment breakdown below' : (splits[0]?.reference || voucher.transactionReference || '-')}
              </Descriptions.Item>

              <Descriptions.Item label="Issued By">
                {voucher.issuedBy?.name || voucher.issuedBy?.email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Issued At">
                {voucher.issuedAt ? dayjs(voucher.issuedAt).format('DD MMM YYYY, h:mm A') : '-'}
              </Descriptions.Item>

              {voucher.remarks && (
                <Descriptions.Item label="Remarks" span={2}>
                  {voucher.remarks}
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* Payment breakdown — mirrors the table printed on the voucher PDF:
                every tranche with its amount and the UTR / cheque number the
                approver entered when the money was released. */}
            {splits.length > 0 && (
              <>
                <Divider orientation="left" plain>Payment Breakdown</Divider>
                <Table
                  size="small"
                  rowKey={(_, i) => i}
                  pagination={false}
                  dataSource={splits}
                  columns={[
                    {
                      title: 'Mode',
                      // The rail the approver recorded (IMPS / NEFT) when there is
                      // one — matching the voucher PDF's Mode column.
                      render: (_, row) => splitModeLabel(row),
                    },
                    {
                      title: 'Amount',
                      dataIndex: 'amount',
                      align: 'right',
                      render: (amount) => new Intl.NumberFormat('en-IN', {
                        style: 'currency', currency: 'INR', minimumFractionDigits: 2,
                      }).format(amount || 0),
                    },
                    {
                      title: 'UTR / Reference',
                      dataIndex: 'reference',
                      render: (reference, row) => (
                        reference || <Text type="secondary">{row.mode === 'CASH' ? '— (cash)' : '—'}</Text>
                      ),
                    },
                  ]}
                  summary={() => (splits.length > 1 ? (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}><Text strong>Total</Text></Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <Text strong>
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency', currency: 'INR', minimumFractionDigits: 2,
                          }).format(splits.reduce((t, s) => t + (Number(s.amount) || 0), 0))}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} />
                    </Table.Summary.Row>
                  ) : null)}
                />
              </>
            )}

            {/* PDF Viewer */}
            <Divider orientation="left" plain>Voucher PDF</Divider>
            
            {voucher.pdfUrl ? (
              <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
                <iframe
                  src={voucher.pdfUrl}
                  width="100%"
                  height="600px"
                  title={`Voucher ${voucher.voucherId} PDF`}
                  style={{ border: 'none' }}
                />
              </div>
            ) : (
              <Alert 
                type="warning" 
                showIcon
                message="PDF not available"
                description="The PDF file could not be loaded. Try regenerating the voucher."
              />
            )}

            {/* Regeneration History */}
            {voucher.regenerationHistory?.length > 0 && (
              <>
                <Divider orientation="left" plain>Regeneration History</Divider>
                <Card size="small">
                  <Text type="secondary">
                    This voucher has been regenerated {voucher.regenerationHistory.length} time(s).
                  </Text>
                  {voucher.regenerationHistory.slice(-3).map((regen, idx) => (
                    <div key={idx} style={{ marginTop: 8, paddingLeft: 16, borderLeft: '2px solid #f0f0f0' }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Regenerated on {dayjs(regen.regeneratedAt).format('DD MMM YYYY, h:mm A')}
                        {regen.reason && ` — ${regen.reason}`}
                      </Text>
                    </div>
                  ))}
                  {voucher.regenerationHistory.length > 3 && (
                    <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                      ... and {voucher.regenerationHistory.length - 3} earlier regenerations
                    </Text>
                  )}
                </Card>
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default VoucherDetailsModal;